import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Button, Table, Tag, Pagination as Paging } from 'antd';
import { isDocumentClassification } from '../../../lib/util';
import { anyRecentProcessingInference } from '../../../lib/util/processing';
import { getFlowListStatus } from '../../../lib/api/flow-api';
import { deleteInference } from '../../../lib/api/aws-api';
import { useInterval } from '../../../lib/useInterval';
import { AddInvoices } from './AddInvoices';
import { getTableColumns } from './InferenceListTable';
import { getTableClassificationColumns } from './InferenceClassificationListTable';
import { Algorithm, Inference, Pagination } from '../../../types';
import { Flow } from '../../../types/flow';
import { SectionTitle } from '../../../components/text/Titles';
import { getFilterStatusLabel, getStatus } from '../../../lib/util/status';
import { useGetFlowInferenceList } from '../../../lib/hooks';
import { ErrorText } from '../../../components/text/ErrorText';

const defaultPaging = {
  pageSize: 5,
  page: 1,
};

type Props = {
  flow: null | Flow;
  algorithm: null | Algorithm;
  algorithmId: string;
  modelId: null | number;
  statusChanged: () => any;
  filter: null | string;
  demo: boolean;
};

const InferenceList = (props: Props) => {
  const { flow, algorithm, algorithmId, modelId, statusChanged, filter } = props;

  const [paginationObject, setPaginationObject] = useState<Pagination>(defaultPaging);
  const [addInvoices, setAddInvoices] = useState<boolean>(false);
  const [inferences, setInferences] = useState<Inference[]>([]);

  const isClassification = !!algorithm && isDocumentClassification(algorithm);

  const { receivedData, totalItemCount, loading, refetch, error } = useGetFlowInferenceList(
    flow?.id || '',
    paginationObject,
    filter || ''
  );

  useEffect(() => {
    setInferences(receivedData);
  }, [receivedData]);

  useInterval(async () => {
    try {
      if (inferences && inferences.length > 0) {
        updateStatus();
      }
    } catch (e) {}
  }, 2000);

  const updateStatus = async () => {
    if (flow && anyRecentProcessingInference(inferences)) {
      // if (isDocumentClassification(algorithm))
      //   return fetch(flow.id, paginationObject);
      const infListStatus = await getFlowListStatus(flow.id);

      const newData = inferences.map((inference, index) => {
        if (inference && getStatus(inference) !== infListStatus[inference.id].status) {
          statusChanged && statusChanged();
          return {
            ...inference,
            status: infListStatus[inference.id].status,
            annotations: infListStatus[inference.id].annotations,
            run:
              algorithm && algorithm.multiflow
                ? {
                    status: infListStatus[inference.id].status,
                    hasUnreliableRecognition: infListStatus[inference.id].hasUnreliableRecognition,
                    enrichmentSuccess: infListStatus[inference.id].enrichmentSuccess,
                    validationSuccess: infListStatus[inference.id].validationSuccess,
                  }
                : undefined,
          };
        }
        return inference;
      });

      setInferences(newData);
    }
  };

  const onClickUrl = useCallback(
    (record: Inference) => {
      if (
        !props.demo &&
        (record.annotations || (algorithm?.multiflow && record.run && record.run.status === 'SUCCESS'))
      ) {
        return `/flows/${algorithmId}/model/${modelId}/${record.id}`;
      }
      if (
        props.demo &&
        (record.annotations || (algorithm?.multiflow && record.run && record.run.status === 'SUCCESS'))
      ) {
        return `/share/${algorithmId}/model/${modelId}/${record.id}`;
      }
    },
    [algorithm, algorithmId, modelId, props.demo]
  );

  const onDelete = async (event: Event, record: Inference) => {
    if (!flow) return;
    event.preventDefault();
    event.stopPropagation();
    await deleteInference(record.id);
    refetch();
    statusChanged && statusChanged();
  };

  const onClose = () => {
    setAddInvoices(false);
  };

  return (
    <Col span={24}>
      {flow && addInvoices && (
        <AddInvoices
          visible={true}
          flow={flow}
          onClose={onClose}
          onFinish={() => {
            refetch();
            statusChanged && statusChanged();
            onClose();
          }}
        />
      )}
      <SectionTitle fontSize={24}>{isClassification ? 'Documents' : 'Invoices'}</SectionTitle>
      <Row className="items-center mt-1 mb-3">
        <Col span={21}>
          <Tag>
            Filter: {filter ? getFilterStatusLabel(filter) : isClassification ? 'All documents' : 'All invoices'}
          </Tag>
        </Col>
        <Col span={3} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            onClick={() => {
              setAddInvoices(true);
            }}
            size={'large'}>
            Add {isClassification ? 'Documents' : 'Invoices'}
          </Button>
        </Col>
      </Row>
      <Row style={{ marginTop: '24px' }}>
        <Col span={24}>
          {error ? (
            <ErrorText>Something went wrong. We could not find the documents you were looking for.</ErrorText>
          ) : (
            <>
              <Table
                className="ant-table-toolbox"
                columns={
                  isDocumentClassification(algorithm)
                    ? getTableClassificationColumns(onClickUrl, onDelete)
                    : getTableColumns(onClickUrl, onDelete, algorithm)
                }
                rowKey={record => record.id}
                dataSource={inferences}
                pagination={false}
                loading={loading}
              />
              <div className="flex justify-end my-4">
                <Col className="mr-4">{totalItemCount} Documents</Col>
                <Paging
                  showSizeChanger={false}
                  defaultCurrent={1}
                  current={paginationObject.page}
                  pageSize={defaultPaging.pageSize}
                  total={totalItemCount}
                  onChange={page => setPaginationObject({ ...defaultPaging, page: page })}
                />
              </div>
            </>
          )}
        </Col>
      </Row>
    </Col>
  );
};

export default InferenceList;
